import { MATRIX_ARCANA } from '@/api/consts'
import {
  MATRIX_GROUPS,
  MATRIX_KARMA_SECTION,
  MATRIX_LOVE_GROUP,
  MATRIX_LOVE_SECTION,
  MATRIX_MISSION_GROUP,
  MATRIX_MONEY_GROUP,
  MATRIX_MONEY_SECTION,
  MATRIX_POINT_COLORS,
  type MatrixSection,
} from '@/api/groups-matrix'
import { Col, Row } from '@/app/components/Flex/Flex'
import { Layout } from '@/app/components/Layout/Layout'
import { Loader } from '@/app/components/Loader/Loader'
import { Matrix } from '@/app/components/Matrix/Matrix'
import { Motion } from '@/app/components/Motion/Motion'
import { Num } from '@/app/components/Num/Num'
import { Text } from '@/app/components/Text/Text'
import { UI } from '@/app/components/UI/UI'
import { MatrixIntent } from '@/app/const'
import { useBack } from '@/app/hooks/useBack'
import { useNavigate, useNavigateSearchParams } from '@/app/hooks/useNavigate'
import ArrowSvg from '@/app/icons/arrow-right.svg?react'
import RadioActiveSvg from '@/app/icons/radio-active.svg?react'
import RadioSvg from '@/app/icons/radio.svg?react'
import { useMatrix } from '@/app/state/matrix'
import { useSession } from '@/app/state/session'
import { getMatrixSectionQuestion } from '@/app/utils/matrix-groups'
import { metrikaGoal } from '@/app/utils/metrika'
import classNames from 'classnames'
import type { FunctionalComponent } from 'preact'
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperType } from 'swiper/types'
import { useShallow } from 'zustand/shallow'
import styles from './index.module.scss'

const DEFAULT_SECTION_ID = MATRIX_GROUPS[0]?.sections[0]?.id ?? null

function validateIntent(value: string): value is MatrixIntent {
  if (
    value === MatrixIntent.Karma
    || value === MatrixIntent.Love
    || value === MatrixIntent.Money
  ) {
    return true
  }
  return false
}

export const MatrixPage: FunctionalComponent = () => {
  const route = useNavigate()
  const [params, setParams] = useNavigateSearchParams()
  const [intent, setIntent] = useState<MatrixIntent | null>(null)

  const intentParam = params.get('intent')
  const profile = useSession(useShallow(s => s.profile))
  const fetch = useMatrix(s => s.fetch)
  const matrix = useMatrix(useShallow(s => s.matrix))
  const back = useBack('~/tools')
  const swiperRef = useRef<SwiperType | null>(null)
  const tabParam = params.get('tab')
  const activeIdxFromParams = Math.max(0, MATRIX_GROUPS.findIndex(item => item.id === tabParam))
  const [activeIdx, setActiveIdx] = useState(activeIdxFromParams)
  const [activeSectionId, setActiveSectionId] = useState<string | null>(DEFAULT_SECTION_ID)
  const [hasSelectedSection, setHasSelectedSection] = useState(false)
  const activeSection = MATRIX_GROUPS
    .flatMap<MatrixSection>(group => group.sections as MatrixSection[])
    .find(section => hasSelectedSection && section.id === activeSectionId)
  const visibleMatrixPoints = activeSection?.points

  const activeGroup = MATRIX_GROUPS.at(activeIdx)

  function getQuestion(question: string, groupId: string) {
    const params = new URLSearchParams({
      q: question,
    })

    if (groupId) {
      params.set('groupId', groupId)
    }

    route(`~/chats/milana?${params.toString()}`)
  }

  useEffect(() => {
    fetch()
  }, [])

  useEffect(() => {
    if (swiperRef.current && swiperRef.current.activeIndex !== activeIdxFromParams) {
      swiperRef.current.slideTo(activeIdxFromParams, 0)
    }

    if (activeIdx === activeIdxFromParams) return

    setActiveIdx(activeIdxFromParams)
  }, [activeIdx, activeIdxFromParams])

  function setTabParam(idx: number) {
    const nextTab = idx === 0 ? null : MATRIX_GROUPS[idx]?.id
    const currentTab = tabParam && MATRIX_GROUPS.some(item => item.id === tabParam)
      ? tabParam
      : null

    if (currentTab === nextTab) return

    setParams((prev) => {
      const next = new URLSearchParams(prev)

      if (nextTab) {
        next.set('tab', nextTab)
      }
      else {
        next.delete('tab')
      }

      return next
    }, {
      replace: true,
    })
  }

  useLayoutEffect(() => {
    if (intentParam && validateIntent(intentParam)) {
      setIntent(intentParam)

      setParams((prev) => {
        const next = new URLSearchParams(prev)
        next.delete('intent')
        return next
      }, {
        replace: true,
      })
    }
  }, [intentParam])

  useLayoutEffect(() => {
    if (intent === MatrixIntent.Money) {
      setTabParam(4)
      setActiveSectionId('moneyCode')
      metrikaGoal('calc_matrix_money')
    }
    else if (intent === MatrixIntent.Love) {
      setTabParam(2)
      setActiveSectionId('loveHeart')
      metrikaGoal('calc_matrix_love')
    }
    else if (intent === MatrixIntent.Karma) {
      setTabParam(1)
      setActiveSectionId('karmaLesson')
      metrikaGoal('calc_matrix_karma')
    }
  }, [intent])

  return (
    <Layout>
      {matrix && intent && (
        <UI.Popup
          onClose={() => {
            setIntent(null)
            setHasSelectedSection(true)
          }}
        >
          <Row gap={15}>
            <Row gap={10}>
              <Col gap={10} alignItems='center'>
                <UI.Avatar size={40} bot='milana' />

                <Row gap={2}>
                  <Text
                    fontSize={16}
                    fontWeight={600}
                  >
                    Милана
                  </Text>
                  <Text fontSize={13} opacity={0.5}>
                    Нумеролог
                  </Text>
                </Row>
              </Col>

              <Row gap={5}>
                <Text fontSize={16}>
                  {intent === MatrixIntent.Money
                    ? (
                      <>
                        {profile?.userName ?? 'Привет'},{' '}
                        <b>твой денежный аркан — {MATRIX_ARCANA[matrix.moneyCenter].name}</b>. Давай разберём, как он проявляется в деньгах
                        и как использовать его сильные стороны.
                      </>
                    )
                    : intent === MatrixIntent.Love
                    ? (
                      <>
                        {profile?.userName ?? 'Привет'},{' '}
                        <b>твоя линия любви {matrix.skyBottom}&#8209;{matrix.relationshipCenter}&#8209;{matrix.balanceCenter}</b>. Давай
                        разберём, как ты проявляешься в отношениях, кого притягиваешь и что может мешать в любви.
                      </>
                    )
                    : (
                      <>
                        {profile?.userName ?? 'Привет'},{' '}
                        <b>твой кармический хвост {matrix.karmaArcana}&#8209;{matrix.skyBottomOuter}&#8209;{matrix.skyBottom}</b>. Давай
                        разберём, какие уроки здесь заложены и какие ситуации могут повторяться, пока ты их не проработаешь.
                      </>
                    )}
                </Text>
              </Row>
            </Row>

            <UI.Button
              type='primary'
              size='m'
              onClick={() => {
                if (intent === MatrixIntent.Money) {
                  getQuestion(getMatrixSectionQuestion(MATRIX_MONEY_SECTION, matrix), MATRIX_MONEY_GROUP.id)
                }
                else if (intent === MatrixIntent.Love) {
                  getQuestion(getMatrixSectionQuestion(MATRIX_LOVE_SECTION, matrix), MATRIX_LOVE_GROUP.id)
                }
                else {
                  getQuestion(getMatrixSectionQuestion(MATRIX_KARMA_SECTION, matrix), MATRIX_MISSION_GROUP.id)
                }
              }}
            >
              Разобрать в чате
            </UI.Button>
          </Row>
        </UI.Popup>
      )}

      <Layout.Content
        fullWidth
        class={styles.desktop}
      >
        {!matrix
          ? (
            <Loader.Container>
              <Loader type='matrix' />
            </Loader.Container>
          )
          : (
            <Motion>
              <Row gap={20}>
                <Col gap={15}>
                  {MATRIX_GROUPS.map((group, idx) => (
                    <UI.Tab
                      key={group.id}
                      active={activeIdx === idx}
                      onClick={() => {
                        setTabParam(idx)
                        swiperRef.current?.slideTo(idx)
                      }}
                    >
                      {group.title}
                    </UI.Tab>
                  ))}
                </Col>

                <div class={styles.grid}>
                  <Row gap={10} class={styles.list}>
                    {activeGroup?.sections.map(section => (
                      <UI.Panel
                        hover
                        key={section.id}
                        class={styles.item}
                        onPointerEnter={() => {
                          setHasSelectedSection(true)
                          setActiveSectionId(section.id)
                        }}
                        onPointerLeave={() => {
                          setHasSelectedSection(true)
                          setActiveSectionId(null)
                        }}
                        onClick={() => {
                          getQuestion(getMatrixSectionQuestion(section, matrix), activeGroup.id)
                        }}
                      >
                        <Row gap={5}>
                          <Col
                            alignItems='center'
                            justifyContent='space-between'
                          >
                            <Col
                              gap={2}
                              alignItems='center'
                            >
                              <Text
                                fontSize={18}
                                fontWeight={600}
                              >
                                {section.title}
                              </Text>

                              <ArrowSvg
                                width={16}
                                height={16}
                                strokeWidth={2.3}
                                class={styles.arrow}
                              />
                            </Col>

                            {matrix && (
                              <Col class={styles.nums}>
                                {section.points.map(point => (
                                  <Num color={MATRIX_POINT_COLORS[point]}>
                                    {matrix[point]}
                                  </Num>
                                ))}
                              </Col>
                            )}
                          </Col>

                          <Text
                            fontSize={16}
                            fontSizeMobile={14}
                            opacity={0.5}
                          >
                            {section.description}
                          </Text>
                        </Row>
                      </UI.Panel>
                    ))}
                  </Row>

                  <div
                    class={styles.chart}
                    // type='transparent'
                  >
                    <Matrix
                      outer
                      outerStroke='#111'
                      matrix={matrix}
                      visiblePoints={visibleMatrixPoints}
                    />
                  </div>
                </div>
              </Row>
            </Motion>
          )}
      </Layout.Content>

      <Layout.Content
        onBack={back}
        class={styles.mobile}
      >
        {!matrix
          ? (
            <Loader.Container>
              <Loader type='matrix' />
            </Loader.Container>
          )
          : (
            <Motion>
              <Layout.Header>
                Матрица судьбы
              </Layout.Header>

              <Row
                gap={15}
              >
                <UI.Panel
                  class={styles.chart}
                  type='transparent'
                >
                  <Matrix
                    outer
                    outerStroke='#111'
                    matrix={matrix}
                    visiblePoints={visibleMatrixPoints}
                  />
                </UI.Panel>

                <UI.Panel
                  class={styles.panel}
                >
                  <UI.Tabs>
                    {MATRIX_GROUPS.map((item, idx) => (
                      <UI.Tab
                        key={item.id}
                        height={48}
                        active={activeIdx === idx}
                        onClick={() => {
                          setTabParam(idx)
                          swiperRef.current?.slideTo(idx)
                        }}
                      >
                        {item.title}
                      </UI.Tab>
                    ))}
                  </UI.Tabs>

                  <div class={styles.swiperWrapper}>
                    <Swiper
                      initialSlide={activeIdxFromParams}
                      slidesPerView={1}
                      className={styles.swiper}
                      onSwiper={(swiper) => {
                        swiperRef.current = swiper

                        if (swiper.activeIndex !== activeIdxFromParams) {
                          swiper.slideTo(activeIdxFromParams, 0)
                        }
                      }}
                      onSlideChange={(swiper) => {
                        setActiveIdx(swiper.activeIndex)
                        setTabParam(swiper.activeIndex)
                      }}
                    >
                      {MATRIX_GROUPS.map((item, groupIdx) => (
                        <SwiperSlide key={item.id} class={styles.swiperSlide}>
                          <div class={styles.table}>
                            {item.sections.map(section => {
                              const isActive = activeIdx === groupIdx
                                && activeSectionId === section.id

                              return (
                                <div
                                  key={section.id}
                                  class={classNames(styles.row, {
                                    [styles.active]: isActive,
                                  })}
                                >
                                  <div
                                    class={styles.cell}
                                    onClick={() => {
                                      setHasSelectedSection(true)
                                      setActiveSectionId(current => current === section.id ? null : section.id)
                                    }}
                                  >
                                    <div
                                      class={classNames(styles.radio, {
                                        [styles.active]: isActive,
                                      })}
                                    >
                                      {isActive ? <RadioActiveSvg /> : <RadioSvg />}
                                    </div>

                                    {section.title}
                                  </div>

                                  <div
                                    class={styles.cell}
                                    onClick={() => {
                                      getQuestion(getMatrixSectionQuestion(section, matrix), item.id)
                                    }}
                                  >
                                    {isActive
                                      ? (
                                        <Motion initialX={-1} initialO={0.3}>
                                          <UI.Button
                                            size='xs'
                                            type='primary'
                                          >
                                            Открыть
                                          </UI.Button>
                                        </Motion>
                                      )
                                      : (
                                        <Col alignItems='center'>
                                          {section.points.map(point => (
                                            <Num color={MATRIX_POINT_COLORS[point]}>
                                              {matrix[point]}
                                            </Num>
                                          ))}
                                          <ArrowSvg class={styles.arrow} />
                                        </Col>
                                      )}
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  </div>
                </UI.Panel>
              </Row>
            </Motion>
          )}
      </Layout.Content>
    </Layout>
  )
}
